import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Tooltip from "@material-ui/core/Tooltip";
import CircularProgress from "@material-ui/core/CircularProgress";

import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";
import CardFooter from "components/Card/CardFooter.js";
import Button from "components/CustomButtons/Button.js";

import ThumbUpAltOutlinedIcon from "@mui/icons-material/ThumbUpAltOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import NotInterested from "@material-ui/icons/NotInterested";
import Chat from "@material-ui/icons/Chat";
import CallOutlinedIcon from "@mui/icons-material/CallOutlined";
import VideoCallOutlinedIcon from "@mui/icons-material/VideoCallOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import TimeAgo from "utils/TimeAgo";
import Configs from "utils/Configs";
import priceImage1 from "assets/img/card-2.jpeg";
import dashboardStyles from "assets/jss/material-dashboard-pro-react/views/dashboardStyle.js";

import { useAuth } from "../../context/AuthContext";
import { useWebSocket } from "../../context/WebSocketContext";
import { useCall } from "../../context/CallContext";
import { useChat } from "../../context/ChatContext";
import { useMutualLikes } from "../../context/MutualLikesContext";
import { showError } from "utils/toastUtils";

const useStyles = makeStyles({
  ...dashboardStyles,
  iconWrapperCircle: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(244, 67, 54, 0.1)",
    borderRadius: "50%",
    padding: "6px",
  },
  iconWrapperCircleGray: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(158, 158, 158, 0.15)",
    borderRadius: "50%",
    padding: "6px",
  },
});

export default function ProfileCard({
  profile,
  onBlockSuccess,
  onUnblockSuccess,
  onLikeSuccess,
  initialLiked,
  initialBlocked,
}) {
  const classes = useStyles();
  const [imageLoaded, setImageLoaded] = useState(false);
  const { sendMessage } = useWebSocket();
  const { user } = useAuth();
  const { openVoiceCall, openCall, actionStatus } = useCall();
  const { openChat } = useChat();
  const { mutualLikes, refreshMutualLikes } = useMutualLikes();

  const userId = user?.user_id || "";

  const [liked, setLiked] = useState(
    initialLiked ?? !!profile.isLikedByCurrentUser,
  );
  const [blocked, setBlocked] = useState(
    initialBlocked ?? !!profile.isBlockedByCurrentUser,
  );

  // ✅ COUNTRY-BASED PHOTO HIDING LOGIC
  const sameCountry =
    user?.country_original &&
    profile?.country_original &&
    user.country_original === profile.country_original;

  const hiddenFromAll = profile.photos_hidden === "yes";
  const hiddenFromCountrymates =
    profile.hide_photos_from_countrymates === "yes" && sameCountry;

  const shouldHidePhoto = hiddenFromAll || hiddenFromCountrymates;

  const hiddenPhotoMessage = hiddenFromAll
    ? "This user has hidden their photos."
    : "This user has hidden photos from people in your country.";

  // ✅ NEW (only for navigation control)
  const isPhotoBlocked =
    shouldHidePhoto || profile.profile_pic_flagged === "yes";

  const handleBlockedNavigation = (e) => {
    e.preventDefault();

    if (shouldHidePhoto) {
      showError(hiddenPhotoMessage, 8000);
    } else {
      showError("This profile photo is currently under review.", 8000);
    }
  };

  useEffect(() => {
    if (initialLiked === undefined) {
      setLiked(!!profile.isLikedByCurrentUser);
    }
    if (initialBlocked === undefined) {
      setBlocked(!!profile.isBlockedByCurrentUser);
    }
  }, [
    profile.isLikedByCurrentUser,
    profile.isBlockedByCurrentUser,
    initialLiked,
    initialBlocked,
  ]);

  useEffect(() => {
    if (
      actionStatus?.peerId === profile.user_id &&
      (actionStatus?.tag === "like" || actionStatus?.tag === "unlike") &&
      actionStatus.status === "success"
    ) {
      const likedResult = actionStatus.tag === "like";
      setLiked(likedResult);
      if (likedResult) {
        onLikeSuccess?.(profile.user_id);
      }
    }
  }, [actionStatus, profile.user_id, onLikeSuccess]);

  useEffect(() => {
    if (
      actionStatus?.peerId === profile.user_id &&
      actionStatus.status === "success"
    ) {
      if (actionStatus.tag === "block") {
        setBlocked(true);
        onBlockSuccess?.(profile.user_id);
      } else if (actionStatus.tag === "unblock") {
        setBlocked(false);
        onUnblockSuccess?.(profile.user_id);
      }
    }
  }, [actionStatus, profile.user_id, onBlockSuccess, onUnblockSuccess]);

  const handleToggleLike = () => {
    if (blocked) {
      showError("You must unblock this profile to like it");
      return;
    }
    const newLiked = !liked;
    setLiked(newLiked);
    if (newLiked) {
      onLikeSuccess?.(profile.user_id);
    }
    sendMessage({
      user_id: userId,
      tag: newLiked ? "like" : "unlike",
      payload: { peer_id: profile.user_id },
    });
    refreshMutualLikes();
  };

  const handleToggleBlock = () => {
    const newBlocked = !blocked;
    sendMessage({
      user_id: userId,
      tag: newBlocked ? "block" : "unblock",
      payload: { peer_id: profile.user_id },
    });
    if (!newBlocked) {
      onUnblockSuccess?.(profile.user_id);
    }
  };

  const handleViewProfile = () => {
    if (blocked) {
      showError("You must unblock this profile to view");
    }
  };

  const handleOpenChat = () => {
    const fullAvatarUrl = profile.profile_pic
      ? `${Configs.baseUrl}${profile.profile_pic}`
      : undefined;

    openChat({
      id: profile.user_id,
      name: profile.pseudo_name,
      avatar: fullAvatarUrl,
    });
  };

  const canCall = mutualLikes.includes(profile.user_id);

  const handleOpenVoiceCall = () => {
    if (canCall) {
      const fullAvatarUrl = profile.profile_pic
        ? `${Configs.baseUrl}${profile.profile_pic}`
        : undefined;

      openVoiceCall({
        id: profile.user_id,
        name: profile.pseudo_name,
        avatar: fullAvatarUrl,
      });
    } else {
      showError("Only mutual likes can voice call");
    }
  };

  const handleOpenVideoCall = () => {
    if (canCall) {
      const fullAvatarUrl = profile.profile_pic
        ? `${Configs.baseUrl}${profile.profile_pic}`
        : undefined;

      openCall({
        id: profile.user_id,
        name: profile.pseudo_name,
        avatar: fullAvatarUrl,
      });
    } else {
      showError("Only mutual likes can video call");
    }
  };

  return (
    <Card product className={classes.cardHover}>
      <CardHeader image className={classes.cardHeaderHover}>
        <Link
          to={
            isPhotoBlocked
              ? "#"
              : `/singles/account-interface/${profile.user_id}`
          }
          state={isPhotoBlocked ? undefined : { profile }}
          style={{ display: "block", position: "relative" }}
          onClick={isPhotoBlocked ? handleBlockedNavigation : undefined}
        >
          {!imageLoaded && (
            <div
              style={{
                width: "100%",
                height: "250px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "absolute",
                background: "#e8ddcf",
                borderRadius: "8px",
              }}
            >
              <CircularProgress color="primary" />
            </div>
          )}

          <div
            style={{
              position: "relative",
              width: "100%",
              height: "250px",
              borderRadius: "8px",
              overflow: "hidden",
            }}
          >
            <div style={{ position: "relative", height: "250px" }}>
              <img
                src={
                  shouldHidePhoto
                    ? priceImage1
                    : profile.profile_pic
                    ? `${Configs.baseUrl}${profile.profile_pic}`
                    : priceImage1
                }
                alt="Profile"
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageLoaded(true)}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  filter:
                    profile.profile_pic_flagged === "yes" || shouldHidePhoto
                      ? "blur(15px)"
                      : "none",
                  display: imageLoaded ? "block" : "none",
                  borderRadius: "8px",
                }}
              />

              {/* FLAGGED PHOTO OVERLAY */}
              {profile.profile_pic_flagged === "yes" && (
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    backgroundColor: "rgba(173, 216, 230, 0.25)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    color: "#fff",
                    fontWeight: "bold",
                    fontSize: "1rem",
                  }}
                >
                  <ErrorOutlineIcon
                    style={{ fontSize: "1.25rem", marginRight: "6px" }}
                  />
                  Photo is flagged
                </div>
              )}

              {/* HIDDEN-PHOTO OVERLAY */}
              {shouldHidePhoto && profile.profile_pic_flagged !== "yes" && (
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    backgroundColor: "rgba(0,0,0,0.45)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    color: "#fff",
                    fontWeight: "bold",
                    fontSize: "0.95rem",
                    textAlign: "center",
                    padding: "10px",
                  }}
                >
                  {hiddenPhotoMessage}
                </div>
              )}
            </div>
          </div>
        </Link>
      </CardHeader>

      <CardBody>
        {/* Top actions: view, like, block */}
        <div
          className={classes.cardHoverUnder}
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <div style={{ display: "flex", gap: "8px" }}>
            {/* VIEW PROFILE */}
            <Tooltip
              title={
                blocked
                  ? "You must unblock this profile to view"
                  : "View Profile"
              }
              placement="bottom"
            >
              <Link
                to={
                  !blocked && !isPhotoBlocked
                    ? `/singles/account-interface/${profile.user_id}`
                    : "#"
                }
                state={!blocked && !isPhotoBlocked ? { profile } : undefined}
                style={{
                  display: "inline-block",
                  pointerEvents: blocked || isPhotoBlocked ? "none" : "auto",
                }}
                onClick={(e) => {
                  if (blocked) {
                    e.preventDefault();
                    handleViewProfile();
                  } else if (isPhotoBlocked) {
                    handleBlockedNavigation(e);
                  }
                }}
              >
                <Button color="info" simple justIcon disabled={blocked}>
                  <AssignmentOutlinedIcon
                    className={classes.underChartIcons}
                    style={{
                      cursor: blocked ? "not-allowed" : "pointer",
                      color: blocked ? "#B0B0B0" : "#9c27b0",
                      fontSize: "24px",
                    }}
                  />
                </Button>
              </Link>
            </Tooltip>

            {/* LIKE */}
            <Tooltip
              title={
                blocked
                  ? "You must unblock this profile to like it"
                  : liked
                  ? "Unlike"
                  : "Like"
              }
              placement="bottom"
            >
              <span>
                <Button
                  color="danger"
                  simple
                  justIcon
                  onClick={handleToggleLike}
                >
                  <span
                    className={
                      liked && !blocked ? classes.iconWrapperCircle : ""
                    }
                  >
                    <ThumbUpAltOutlinedIcon
                      className={classes.underChartIcons}
                      style={{
                        cursor: blocked ? "not-allowed" : "pointer",
                        color: blocked
                          ? "#B0B0B0"
                          : liked
                          ? "#f44336"
                          : "#9c27b0",
                        fontSize: "24px",
                      }}
                    />
                  </span>
                </Button>
              </span>
            </Tooltip>

            {/* BLOCK */}
            <Tooltip title={blocked ? "Unblock" : "Block"} placement="bottom">
              <Button
                color="danger"
                simple
                justIcon
                onClick={handleToggleBlock}
              >
                <span className={blocked ? classes.iconWrapperCircleGray : ""}>
                  <NotInterested
                    className={classes.underChartIcons}
                    style={{
                      cursor: "pointer",
                      color: "#f44336",
                      fontSize: "24px",
                    }}
                  />
                </span>
              </Button>
            </Tooltip>
          </div>
        </div>

        {/* NAME / GENDER / AGE */}
        <h4 className={classes.cardProductTitle}>
          <a
            href="#pablo"
            onClick={(e) => e.preventDefault()}
            style={{ fontSize: "1.1rem", textDecoration: "none" }}
          >
            <span style={{ color: "#CC0099" }}>
              {profile.pseudo_name
                ? profile.pseudo_name.charAt(0).toUpperCase() +
                  profile.pseudo_name.slice(1).toLowerCase()
                : "Unknown Name"}
            </span>
            <span style={{ color: "#000", margin: "0 4px" }}>•</span>
            <span style={{ color: "#000" }}>
              {profile.gender
                ? profile.gender.charAt(0).toUpperCase() +
                  profile.gender.slice(1).toLowerCase()
                : "n/a"}
            </span>
            <span style={{ color: "#000", margin: "0 4px" }}>/</span>
            <span style={{ color: "#000" }}>{profile.age || "n/a"}</span>
          </a>
        </h4>

        {/* ABOUT ME */}
        <p
          className={classes.cardProductDesciprion}
          style={{
            maxHeight: "65px",
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
          }}
        >
          {profile.about_me
            ? profile.about_me.length > 200
              ? `${profile.about_me.slice(0, 200)}...`
              : profile.about_me
            : "No about me information available"}
        </p>

        {/* LAST SEEN / ONLINE */}
        <p
          className={classes.cardProductDesciprion}
          style={{ color: "#D76213" }}
        >
          {profile.online_status === "online" ? (
            "I am available"
          ) : (
            <>
              Last seen <TimeAgo date={profile.last_seen || "n/a"} />
            </>
          )}
        </p>

        {/* ⭐⭐⭐ NEW POSITION — COMMUNICATION BUTTONS ⭐⭐⭐ */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "20px",
            marginTop: "10px",
            marginBottom: "5px",
          }}
        >
          <Tooltip title="Message" placement="top">
            <Chat
              style={{
                cursor: "pointer",
                color: "#38b000 ",
                fontSize: "18px",
              }}
              onClick={handleOpenChat}
            />
          </Tooltip>

          <Tooltip
            title={
              canCall ? "Voice Call" : "Like this person to allow voice call"
            }
            placement="top"
          >
            <CallOutlinedIcon
              style={{
                cursor: canCall ? "pointer" : "not-allowed",
                color: canCall ? "#38b000 " : "#B0B0B0",
                fontSize: "18px",
              }}
              onClick={handleOpenVoiceCall}
            />
          </Tooltip>

          <Tooltip
            title={
              canCall ? "Video Call" : "Like this person to allow video call"
            }
            placement="top"
          >
            <VideoCallOutlinedIcon
              style={{
                cursor: canCall ? "pointer" : "not-allowed",
                color: canCall ? "#38b000 " : "#B0B0B0",
                fontSize: "18px",
              }}
              onClick={handleOpenVideoCall}
            />
          </Tooltip>
        </div>
      </CardBody>

      <CardFooter product>
        {profile.country && (
          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <img
              src={`${Configs.flagCdnBaseUrl}/${profile.country.toLowerCase()}.png`}
              alt="Country flag"
              width="20"
              height="14"
              style={{ borderRadius: "4px" }}
            />
            <span>{profile.country_name || "Unknown Location"}</span>
          </div>
        )}

        <div className={classes.price}>
          <div
            className={classes.productStats}
            style={{ display: "flex", alignItems: "center" }}
          >
            <span
              style={{
                width: "11px",
                height: "11px",
                borderRadius: "50%",
                backgroundColor:
                  profile.online_status === "online"
                    ? "#38b000"
                    : profile.online_status === "away"
                    ? "orange"
                    : "gray",
                marginRight: "6px",
                display: "inline-block",
              }}
            />
            <span>{profile.online_status}</span>
          </div>
        </div>
      </CardFooter>
    </Card>
  );
}
